import builder from '@sanity/image-url'
import {
  type ImageFormat,
  type SanityImageSource,
} from '@sanity/image-url/lib/types/types'
import { type ImageLoaderProps } from 'next/image'

import { type SanityFrameBuilderImage } from '@frame-builder/data/sanity/queries/types/image'
import { sanityDataset, sanityProjectId } from './sanity'

interface SourceOptionsProps {
  width?: number
  height?: number
  format?: ImageFormat
  quality?: number
  fitMax?: boolean
  autoFormat?: boolean
}

export interface ImageDimensions {
  width?: number
  height?: number
}

/**
 * Builds image source URL from image data and options.
 */
export function getSanityImageUrl(
  image?: SanityImageSource,
  options: SourceOptionsProps = {},
) {
  if (!sanityProjectId || !sanityDataset || !image) {
    return
  }

  try {
    const { width, height, format, quality, fitMax, autoFormat } = options
    let imageUrlBuilder = builder({
      projectId: sanityProjectId,
      dataset: sanityDataset,
    }).image(image)

    if (width) {
      imageUrlBuilder = imageUrlBuilder.width(Math.round(width))
    }

    if (height) {
      imageUrlBuilder = imageUrlBuilder.height(Math.round(height))
    }

    if (format) {
      imageUrlBuilder = imageUrlBuilder.format(format)
    }

    if (quality) {
      imageUrlBuilder = imageUrlBuilder.quality(quality)
    }

    if (fitMax) {
      imageUrlBuilder = imageUrlBuilder.fit('max')
    }

    if (autoFormat) {
      imageUrlBuilder = imageUrlBuilder.auto('format')
    }

    return imageUrlBuilder.url()
  } catch (error) {
    console.error(error)
  }
}

/**
 * Gets image loader for Next.js image component.
 */
export const getBasicImageLoader = ({ src }: ImageLoaderProps) => {
  return src
}

/**
 * Gets image loader for Next.js image component.
 */
export const sanityImageLoader = ({
  src,
  width,
  quality,
}: ImageLoaderProps) => {
  const image = JSON.parse(src) as unknown as SanityFrameBuilderImage
  const options: SourceOptionsProps = {
    width,
    quality,
    fitMax: true,
    autoFormat: true,
  }

  // Calculate image height
  if (image.asset?.metadata?.dimensions?.aspectRatio) {
    options.height = width / image.asset.metadata.dimensions.aspectRatio
  }

  return getSanityImageUrl(image, options) ?? ''
}

/**
 * Gets scalar value from any value.
 */
export const getScalarValue = (value?: string) => {
  const scalarValue = value?.match(/-?[\d.]+/g)?.join('')

  if (!scalarValue) {
    return
  }

  return Number(scalarValue)
}

/**
 * Extracts CSS unit from string value
 */
export function getCSSUnit(value?: string) {
  return value
    ?.match(/(%|px|rem|em|vh|vw)/gi)
    ?.filter(Boolean)
    ?.join('')
}

/**
 * Gets fixed width CSS rule from fixed height CSS rule and aspect ratio.
 */
export const getFixedWidth = (aspectRatio: number, fixedHeight?: string) => {
  const heightValue = getScalarValue(fixedHeight)

  if (!heightValue) {
    return
  }

  const heightUnit = getCSSUnit(fixedHeight)

  return `${heightValue / aspectRatio}${heightUnit || 'px'}`
}

/**
 * Get image dimensions based on its size and fixed height.
 */
export const getImageDimensions = (
  image: SanityFrameBuilderImage,
): ImageDimensions => {
  if (!image.asset?.metadata?.dimensions) {
    return {}
  }

  return {
    width: image.asset.metadata.dimensions.width,
    height: image.asset.metadata.dimensions.height,
  }
}
